import React, { useState } from 'react';

import { RecordContextProvider, useRecordContext } from 'react-admin';

import {
  Box,
  Button,
  Card,
  Collapse,
  Grid,
  Typography,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { get, isEmpty } from 'lodash';
import { getName } from 'i18n-iso-countries';

import Answer from '../Answer';
import FirstName from './FirstName';
import LastName from './LastName';
import DateOfBirth from './DateOfBirth';
import Address from './Address';
import City from './City';
import PostalCode from './PostalCode';
import Residence from './Residence';
import NationalIdentifier from './NationalIdentifier';
import Citizenship from './Citizenship';
import TaxResidency from './TaxResidency';
import Pep from './Pep';
import Custom from './Custom';

import languages, { Languages } from '../../../../constants/languages';
import questions, { Questions } from '../../../../constants/questions';
import { legalFormTypeChoices } from '../../../../constants/legalForms';
import {
  controlTreeControlFields,
  controlTreeControlFieldList,
  ControlTreeNode,
  controlTreeNodeTypes,
  defaultControlTreeBusinessFields,
  defaultControlTreeIndividualFields,
} from '../../../../constants/controlTree';
import { ControlTreeParameters, CustomEntry } from '../../questionParameters';

const LABEL = {
  [languages.EN]: 'Control tree',
  [languages.SV]: 'Kontrollträd',
  [languages.DE]: 'Kontrollbaum',
};

const TEXTS = {
  controlOver: {
    [languages.EN]: 'Control over',
    [languages.SV]: 'Kontroll över',
    [languages.DE]: 'Kontrolle über',
  },
  [controlTreeControlFields.CAPITAL_SHARE]: {
    [languages.EN]: 'Share of capital',
    [languages.SV]: 'Andel av kapitalet',
    [languages.DE]: 'Kapitalanteil',
  },
  [controlTreeControlFields.VOTING_SHARE]: {
    [languages.EN]: 'Share of votes',
    [languages.SV]: 'Andel av rösterna',
    [languages.DE]: 'Stimmrechtsanteil',
  },
  [controlTreeControlFields.OTHER_CONTROL]: {
    [languages.EN]: 'Other control',
    [languages.SV]: 'Annan kontroll',
    [languages.DE]: 'Sonstige Kontrolle',
  },
  [questions.Q_COMPANY_NAME]: {
    [languages.EN]: 'Company name',
    [languages.SV]: 'Företagsnamn',
    [languages.DE]: 'Firmenname',
  },
  [questions.Q_ORG_NUMBER]: {
    [languages.EN]: 'Organization number',
    [languages.SV]: 'Organisationsnummer',
    [languages.DE]: 'Organisationsnummer',
  },
  [questions.Q_LEGAL_FORM]: {
    [languages.EN]: 'Legal form',
    [languages.SV]: 'Bolagsform',
    [languages.DE]: 'Rechtsform',
  },
  [questions.Q_TIN]: {
    [languages.EN]: 'Tax Registration Number (TIN)',
    [languages.SV]: 'Skatteregistreringsnummer (TIN)',
    [languages.DE]: 'Steueridentifikationsnummer (TIN)',
  },
  [questions.Q_WEBSITE]: {
    [languages.EN]: 'Website',
    [languages.SV]: 'Hemsida',
    [languages.DE]: 'Webseite',
  },
  unnamed: {
    [languages.EN]: 'Unnamed',
    [languages.SV]: 'Namnlös',
    [languages.DE]: 'Ohne Namen',
  },
} as const;

const nodeName = (node: ControlTreeNode | undefined): string => {
  if (!node) return '';
  if (node.type === controlTreeNodeTypes.BUSINESS) return node.companyName ?? '';
  return [node.firstName, node.lastName].filter(Boolean).join(' ');
};

const NestedValue = ({ label, value }: { label: string; value?: string }) => (
  <Grid size={{ xs: 12, md: 3 }}>
    <Box display="flex" flexDirection="column">
      <Typography variant="label">{label}</Typography>
      <Typography>{value || '-'}</Typography>
    </Box>
  </Grid>
);

const NodeField = ({
  questionId,
  node,
  language,
  parameters,
}: {
  questionId: Questions;
  node: ControlTreeNode;
  language: Languages;
  parameters?: Record<string, any>;
}) => {
  const fieldParameters = parameters?.[questionId];
  switch (questionId) {
    case questions.Q_COMPANY_NAME:
    case questions.Q_TIN:
    case questions.Q_WEBSITE: {
      const source = { [questions.Q_COMPANY_NAME]: 'companyName', [questions.Q_TIN]: 'tin', [questions.Q_WEBSITE]: 'website' }[questionId];
      return (
        <NestedValue
          label={fieldParameters?.label ?? TEXTS[questionId][language]}
          value={get(node, source) as string | undefined}
        />
      );
    }
    case questions.Q_ORG_NUMBER: {
      const country = get(node, 'organizationNumber.country') as string | undefined;
      const number = get(node, 'organizationNumber.registrationNumber') as string | undefined;
      return (
        <NestedValue
          label={fieldParameters?.fields?.registrationNumber?.label ?? TEXTS[questionId][language]}
          value={[number, country && getName(country, language)].filter(Boolean).join(', ')}
        />
      );
    }
    case questions.Q_LEGAL_FORM: {
      const legalForm = get(node, 'legalForm') as { options?: string; other?: string } | undefined;
      const choice = legalFormTypeChoices(language).find(({ id }) => id === legalForm?.options);
      return (
        <NestedValue
          label={fieldParameters?.label ?? TEXTS[questionId][language]}
          value={legalForm?.other ?? choice?.name}
        />
      );
    }
    case questions.Q_FIRST_NAME: return <FirstName.Nested extraParameters={fieldParameters} />;
    case questions.Q_LAST_NAME: return <LastName.Nested extraParameters={fieldParameters} />;
    case questions.Q_DATE_OF_BIRTH: return <DateOfBirth.Nested extraParameters={fieldParameters} />;
    case questions.Q_ADDRESS: return <Address.Nested extraParameters={fieldParameters} />;
    case questions.Q_CITY: return <City.Nested extraParameters={fieldParameters} />;
    case questions.Q_POSTAL_CODE: return <PostalCode.Nested extraParameters={fieldParameters} />;
    case questions.Q_RESIDENCE: return <Residence.Nested extraParameters={fieldParameters} />;
    case questions.Q_NATIONAL_IDENTIFIER:
      return <NationalIdentifier.Nested extraParameters={fieldParameters} />;
    case questions.Q_CITIZENSHIP: return <Citizenship.Nested extraParameters={fieldParameters} />;
    case questions.Q_TAX_RESIDENCY:
      return <TaxResidency.Nested extraParameters={fieldParameters} />;
    case questions.Q_PEP: return <Pep.Nested extraParameters={fieldParameters} />;
    default: return null;
  }
};

const NodeDetails = ({
  node,
  parent,
  language,
  extraParameters,
}: {
  node: ControlTreeNode;
  parent?: ControlTreeNode;
  language: Languages;
  extraParameters?: ControlTreeParameters;
}) => {
  const isBusiness = node.type === controlTreeNodeTypes.BUSINESS;
  const parameters = isBusiness ? extraParameters?.business : extraParameters?.individual;
  const fields = parameters?.fields
    ?? (isBusiness ? defaultControlTreeBusinessFields : defaultControlTreeIndividualFields);
  const controlFields = controlTreeControlFieldList;

  return (
    <Grid container spacing={6} py="1%" px="1%">
      {parent && (
        <>
          <Grid size={12}>
            <Typography variant="subtitle2">
              {`${TEXTS.controlOver[language]} ${nodeName(parent) || TEXTS.unnamed[language]}`}
            </Typography>
          </Grid>
          {controlFields.map((field) => {
            const value = node.control?.[field];
            return (
              <NestedValue
                key={field}
                label={extraParameters?.control?.[field]?.label ?? TEXTS[field][language]}
                value={value === undefined || field === controlTreeControlFields.OTHER_CONTROL
                  ? value as string | undefined
                  : `${value} %`}
              />
            );
          })}
          <RecordContextProvider value={{ language, ...node.control }}>
            {extraParameters?.control?.customQuestions?.map((customQuestion: CustomEntry) => (
              <Custom.Nested key={customQuestion.id} extraParameters={customQuestion} />
            ))}
          </RecordContextProvider>
          <Grid size={12} />
        </>
      )}
      <RecordContextProvider value={{ language, ...node }}>
        {fields.map((questionId) => (
          <NodeField
            key={questionId}
            questionId={questionId}
            node={node}
            language={language}
            parameters={parameters}
          />
        ))}
        {parameters?.customQuestions?.map((customQuestion: CustomEntry) => (
          <Custom.Nested key={customQuestion.id} extraParameters={customQuestion} />
        ))}
      </RecordContextProvider>
    </Grid>
  );
};

const TreeNode = ({
  node,
  nodes,
  language,
  extraParameters,
}: {
  node: ControlTreeNode;
  nodes: ControlTreeNode[];
  language: Languages;
  extraParameters?: ControlTreeParameters;
}) => {
  const [open, setOpen] = useState(false);
  const owners = nodes.filter(({ parentId }) => parentId === node.id);
  const parent = nodes.find(({ id }) => id === node.parentId);
  const detailsId = `control-tree-node-${node.id}`;

  return (
    <Box component="li" sx={{ listStyle: 'none' }}>
      <Card variant="outlined" sx={{ m: 0 }}>
        <Button
          onClick={() => setOpen((current) => !current)}
          aria-expanded={open}
          aria-controls={detailsId}
          color="inherit"
          fullWidth
          sx={{
            justifyContent: 'flex-start', textTransform: 'none', gap: 2, px: 3,
          }}
          startIcon={(
            <ChevronRightIcon
              sx={{ transform: open ? 'rotate(90deg)' : undefined, transition: 'transform 0.2s' }}
            />
          )}
        >
          {node.type === controlTreeNodeTypes.BUSINESS
            ? <BusinessIcon fontSize="small" color="action" />
            : <PersonIcon fontSize="small" color="action" />}
          <Typography variant="h6" component="span">
            {nodeName(node) || TEXTS.unnamed[language]}
          </Typography>
        </Button>
        <Collapse in={open} id={detailsId}>
          <Box px={3} pb={3}>
            <NodeDetails
              node={node}
              parent={parent}
              language={language}
              extraParameters={extraParameters}
            />
          </Box>
        </Collapse>
      </Card>
      {owners.length > 0 && (
        <Box
          component="ul"
          sx={{
            m: 0,
            pl: 4,
            ml: 3,
            pt: 2,
            borderLeft: 2,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {owners.map((owner) => (
            <TreeNode
              key={owner.id}
              node={owner}
              nodes={nodes}
              language={language}
              extraParameters={extraParameters}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const ValueField = ({ language, extraParameters }: {
  language?: Languages;
  extraParameters?: ControlTreeParameters;
}) => {
  const record = useRecordContext();
  const nodes = get(record, 'value') as ControlTreeNode[] | undefined;
  const root = nodes?.find(({ parentId }) => parentId === undefined);

  if (isEmpty(nodes) || !nodes || !root) return null;

  return (
    <Box component="ul" sx={{ m: 0, p: 0, width: '100%' }}>
      <TreeNode
        node={root}
        nodes={nodes}
        language={language ?? languages.EN}
        extraParameters={extraParameters}
      />
    </Box>
  );
};

const ControlTree = ({ extraParameters }: { extraParameters?: ControlTreeParameters }) => {
  const record = useRecordContext();
  const language = record?.language as Languages;

  return (
    <Answer
      label={extraParameters?.label ?? LABEL[language]}
      isComplexType
      datapoint={null}
      identificationInfo={undefined}
      source="controlTree"
    >
      <ValueField language={language} extraParameters={extraParameters} />
    </Answer>
  );
};

ControlTree.ValueField = ValueField;

export default ControlTree;
